/**
 * Masterclass Context Provider
 *
 * Manages the global state of the masterclass application including:
 * - User progression through cases (visited slides, answers, scores)
 * - Selected case
 * - Video states (intro, confirmation, completion)
 * - User profile
 *
 * Synchronization with Supabase via React Query:
 * - Loads progression on mount and auth changes
 * - Auto-saves progress changes using mutations
 * - Automatic migration from old data format (indices → IDs)
 *
 * @example
 * ```tsx
 * const { selectedCase, updateCaseProgress, caseProgress } = useMasterclass();
 * updateCaseProgress(1, 'slide-intro');
 * ```
 */
import * as Sentry from '@sentry/react';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';

import {
  useAuthUser,
  useCaseProgress,
  useAnswers,
  useProfile,
  useUpsertCaseProgress,
  useInsertAnswer,
  useDeleteAnswers,
  useResetCaseProgress,
  useUpsertCaseStatistics,
} from '@/hooks/queries';
import { supabase } from '@/integrations/supabase/client';
import { captureSentry } from '@/utils/sentryHelper';

export interface CaseProgress {
  caseId: string;
  completed: boolean;
  currentSlideId: string;
  visitedSlideIds: string[];
  celebration_shown?: boolean;
}

export interface Answer {
  id: string;
  case_progress_id: string;
  slide_id: string;
  user_id: string;
  answer_id: string;
  created_at: string;
  case_id: string;
}

interface UserProfile {
  full_name?: string;
  email?: string;
  username?: string;
}

interface MasterclassContextType {
  selectedCase: string | null;
  setSelectedCase: (caseKey: string | null) => void;
  hasSeenIntro: boolean;
  setHasSeenIntro: (seen: boolean) => void;
  caseProgress: Record<string, CaseProgress>;
  updateCaseProgress: (caseId: string, slideId: string) => Promise<void>;
  markCaseCompleted: (caseId: string) => Promise<void>;
  markCelebrationShown: (caseId: string) => Promise<void>;
  showIntroVideo: boolean;
  setShowIntroVideo: (show: boolean) => void;
  showConfirmationVideo: boolean;
  setShowConfirmationVideo: (show: boolean) => void;
  showCompletionVideo: boolean;
  setShowCompletionVideo: (show: boolean) => void;
  showLoadingAnimation: boolean;
  setShowLoadingAnimation: (loading: boolean) => void;
  userProfile: UserProfile | null;
  answers: Answer[];
  recordAnswer: (caseId: string, slideId: string, answerId: string) => Promise<void>;
  getAnswersForSlide: (caseId: string, slideId: string) => Answer[];
  loading: boolean;
  instantiateCaseProgress: (caseId: string) => Promise<void>;
  resetCaseProgress: (caseId: string) => Promise<void>;
  resetAccreditationUserSlidePosition: (caseId: string) => Promise<void>;
  isDemo: boolean;
}

const MasterclassContext = createContext<MasterclassContextType | undefined>(undefined);

export const MasterclassProvider = ({ children }: { children: ReactNode }) => {
  const [selectedCase, setSelectedCase] = useState<string | null>(null);
  const [hasSeenIntro, setHasSeenIntro] = useState(false);
  const [showIntroVideo, setShowIntroVideo] = useState(false);
  const [showConfirmationVideo, setShowConfirmationVideo] = useState(false);
  const [showCompletionVideo, setShowCompletionVideo] = useState(false);
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);

  // React Query hooks
  const { data: user } = useAuthUser();
  const userId = user?.id || null;

  const { data: profileData } = useProfile(userId);
  const { data: progressData, isLoading: progressLoading } = useCaseProgress(userId);
  const { data: answersData, isLoading: answersLoading } = useAnswers(userId);

  const upsertProgressMutation = useUpsertCaseProgress();
  const insertAnswerMutation = useInsertAnswer();
  const deleteAnswersMutation = useDeleteAnswers();
  const resetProgressMutation = useResetCaseProgress();
  const upsertStatsMutation = useUpsertCaseStatistics();

  const loading = progressLoading || answersLoading;

  // Reset confirmation video state on mount
  useEffect(() => {
    setShowConfirmationVideo(false);
  }, []);

  // Set Sentry user context
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email });
    }
  }, [user]);

  // Build case progress map from query data
  const caseProgress = useMemo(() => {
    if (!progressData) return {};

    const progressMap: Record<string, CaseProgress> = {};
    progressData.forEach((progress) => {
      progressMap[progress.case_id] = {
        caseId: progress.case_id,
        completed: progress.completed || false,
        currentSlideId: (progress.current_slide_id as string) || '',
        visitedSlideIds: (progress.visited_slide_ids as string[]) || [],
        celebration_shown: progress.celebration_shown || false,
      };
    });

    return progressMap;
  }, [progressData]);

  // Build user profile
  const userProfile = useMemo<UserProfile | null>(() => {
    if (!profileData) return null;
    return {
      full_name: profileData.full_name ?? undefined,
      email: profileData.email ?? undefined,
      username: profileData.username ?? undefined,
    };
  }, [profileData]);

  // Build answers array
  const answers = useMemo<Answer[]>(() => {
    return answersData || [];
  }, [answersData]);

  const updateCaseProgress = useCallback(
    async (caseId: string, slideId: string) => {
      if (!userId) return;

      const existingProgress = caseProgress[caseId];
      const visitedSlideIds = [...(existingProgress?.visitedSlideIds || [])];

      // Add current slide if not already visited
      if (!visitedSlideIds.includes(slideId)) {
        visitedSlideIds.push(slideId);
      }

      const isCompleted = existingProgress?.completed || false;

      await upsertProgressMutation.mutateAsync({
        userId,
        caseId,
        updates: {
          current_slide_id: slideId,
          visited_slide_ids: visitedSlideIds,
          completed: isCompleted,
        },
      });
    },
    [userId, caseProgress]
  );

  const markCaseCompleted = useCallback(
    async (caseId: string) => {
      if (!userId) return;

      const existingProgress = caseProgress[caseId];

      // Update progress to completed
      await upsertProgressMutation.mutateAsync({
        userId,
        caseId,
        updates: {
          completed: true,
          current_slide_id: existingProgress?.currentSlideId,
          visited_slide_ids: existingProgress?.visitedSlideIds,
          celebration_shown: false,
        },
      });

      // Track completion in statistics
      try {
        const { data: stats } = await supabase
          .from('case_statistics')
          .select('completions')
          .eq('user_id', userId)
          .eq('case_id', caseId)
          .maybeSingle();

        await upsertStatsMutation.mutateAsync({
          userId,
          caseId,
          updates: {
            completions: (stats?.completions || 0) + 1,
          },
        });
      } catch (error) {
        console.error('Error tracking completion:', error);
        captureSentry({
          title: 'Failed to track case completion',
          error,
          detail: { caseId, userId },
        });
      }
    },
    [userId, caseProgress]
  );

  const markCelebrationShown = useCallback(
    async (caseId: string) => {
      if (!userId) return;

      await upsertProgressMutation.mutateAsync({
        userId,
        caseId,
        updates: {
          celebration_shown: true,
        },
      });
    },
    [userId]
  );

  const instantiateCaseProgress = useCallback(
    async (caseId: string) => {
      if (!userId || caseProgress[caseId]) return;

      await upsertProgressMutation.mutateAsync({
        userId,
        caseId,
        updates: {
          current_slide_id: '',
          visited_slide_ids: [],
          completed: false,
        },
      });
    },
    [userId, caseProgress]
  );

  const resetCaseProgress = useCallback(
    async (caseId: string) => {
      if (!userId) {
        Sentry.captureMessage('No user found when resetting case progress', {
          tags: {
            component: 'MasterclassContext',
            operation: 'resetCaseProgress',
          },
        });
        return;
      }

      await deleteAnswersMutation.mutateAsync({ userId, caseId });
      await resetProgressMutation.mutateAsync({ userId, caseId });
    },
    [userId]
  );

  const resetAccreditationUserSlidePosition = useCallback(
    async (caseId: string) => {
      if (!userId) {
        Sentry.captureMessage('No user found when resetting slide position', {
          tags: {
            component: 'MasterclassContext',
            operation: 'resetAccreditationUserSlidePosition',
          },
        });
        return;
      }

      await upsertProgressMutation.mutateAsync({
        userId,
        caseId,
        updates: {
          current_slide_id: '',
          visited_slide_ids: [],
          celebration_shown: false,
        },
      });
    },
    [userId]
  );

  const recordAnswer = useCallback(
    async (caseId: string, slideId: string, answerId: string) => {
      if (!userId) return;

      // Get case progress ID
      const { data: existingProgress } = await supabase
        .from('case_progress')
        .select('id')
        .eq('user_id', userId)
        .eq('case_id', caseId)
        .maybeSingle();

      if (!existingProgress) {
        console.error('Error: No case progress found for user and case');
        captureSentry({
          title: 'No case progress found when recording answer',
          detail: { caseId, slideId, answerId, userId },
        });
        return;
      }

      await insertAnswerMutation.mutateAsync({
        caseProgressId: existingProgress.id,
        slideId,
        userId,
        answerId,
        caseId,
      });
    },
    [userId]
  );

  const getAnswersForSlide = useCallback(
    (caseId: string, slideId: string): Answer[] => {
      const caseAnswers = answers.filter((answer) => answer.case_id === caseId);
      return caseAnswers.filter((answer) => answer.slide_id === slideId);
    },
    [answers]
  );

  const isDemo = profileData?.username?.startsWith('demo') || false;

  return (
    <MasterclassContext.Provider
      value={{
        selectedCase,
        setSelectedCase,
        hasSeenIntro,
        setHasSeenIntro,
        caseProgress,
        updateCaseProgress,
        markCaseCompleted,
        markCelebrationShown,
        showIntroVideo,
        setShowIntroVideo,
        showConfirmationVideo,
        setShowConfirmationVideo,
        showCompletionVideo,
        setShowCompletionVideo,
        showLoadingAnimation,
        setShowLoadingAnimation,
        userProfile,
        answers,
        recordAnswer,
        getAnswersForSlide,
        loading,
        instantiateCaseProgress,
        resetCaseProgress,
        resetAccreditationUserSlidePosition,
        isDemo,
      }}
    >
      {children}
    </MasterclassContext.Provider>
  );
};

export const useMasterclass = () => {
  const context = useContext(MasterclassContext);
  if (!context) {
    throw new Error('useMasterclass must be used within MasterclassProvider');
  }
  return context;
};
